import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { loadProfile } from "./lib/storage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthScreen from "./pages/AuthScreen";
import LandingPage from "./pages/LandingPage";
import ProfileSetup from "./pages/ProfileSetup";
import Dashboard from "./pages/Dashboard";
import LevelSelect from "./pages/LevelSelect";
import Practice from "./pages/Practice";
import Results from "./pages/Results";
import Medallas from "./pages/Medallas";
import FraccionesSubSelect from "./pages/FraccionesSubSelect";
import DecimalesSubSelect from "./pages/DecimalesSubSelect";
import Primaria from "./pages/Primaria";
import SecundariaTopics from "./pages/SecundariaTopics";
import AlgebraSubSelect from "./pages/AlgebraSubSelect";
import GeometriaSubSelect from "./pages/GeometriaSubSelect";
import Upgrade from "./pages/Upgrade";
import BillingSuccess from "./pages/BillingSuccess";
import MiSuscripcion from "./pages/MiSuscripcion";
import Terminos from "./pages/Terminos";
import AdminStats from "./pages/AdminStats";
import Ranking from "./pages/Ranking";
import { EntitlementProvider } from "./contexts/EntitlementContext";

function LoadingScreen() {
  return (
    <div className="kid-bg min-h-screen flex items-center justify-center">
      <div className="font-heading text-2xl text-[#5C5C60]">Cargando…</div>
    </div>
  );
}

function RequireSession({ children, profile }) {
  const { status } = useAuth();
  if (status === "loading") return <LoadingScreen />;
  if (status === "none") return <Navigate to="/login" replace />;
  if (!profile) return <Navigate to="/perfil" replace />;
  return children;
}

function Routed({ profile, refresh }) {
  const { status } = useAuth();
  const navigate = useNavigate();

  // Sync profile state if it ever changes externally
  useEffect(() => {
    // refresh in case AuthContext just restored a cloud profile to localStorage
    // (handled by AuthContext.afterAuth/bootstrap calling saveProfile)
  }, []);

  if (status === "loading") return <LoadingScreen />;

  return (
    <Routes>
      <Route
        path="/"
        element={
          status === "none" ? (
            <LandingPage />
          ) : profile ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/perfil" replace />
          )
        }
      />
      <Route
        path="/login"
        element={
          status === "none" ? (
            <AuthScreen />
          ) : profile ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/perfil" replace />
          )
        }
      />
      <Route
        path="/perfil"
        element={
          status === "none" ? (
            <Navigate to="/login" replace />
          ) : profile ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <ProfileSetup onCreate={refresh} />
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireSession profile={profile}>
            <Dashboard profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/primaria"
        element={
          <RequireSession profile={profile}>
            <Primaria profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria"
        element={
          <RequireSession profile={profile}>
            <SecundariaTopics profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/algebra"
        element={
          <RequireSession profile={profile}>
            <AlgebraSubSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/algebra/:subOp"
        element={
          <RequireSession profile={profile}>
            <LevelSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/algebra/:subOp/:levelId"
        element={
          <RequireSession profile={profile}>
            <Practice profile={profile} onUpdate={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/geometria"
        element={
          <RequireSession profile={profile}>
            <GeometriaSubSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/geometria/:subOp"
        element={
          <RequireSession profile={profile}>
            <LevelSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/secundaria/geometria/:subOp/:levelId"
        element={
          <RequireSession profile={profile}>
            <Practice profile={profile} onUpdate={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/fracciones"
        element={
          <RequireSession profile={profile}>
            <FraccionesSubSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/fracciones/:subOp"
        element={
          <RequireSession profile={profile}>
            <LevelSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/fracciones/:subOp/:levelId"
        element={
          <RequireSession profile={profile}>
            <Practice profile={profile} onUpdate={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/decimales"
        element={
          <RequireSession profile={profile}>
            <DecimalesSubSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/decimales/:subOp"
        element={
          <RequireSession profile={profile}>
            <LevelSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/decimales/:subOp/:levelId"
        element={
          <RequireSession profile={profile}>
            <Practice profile={profile} onUpdate={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/:operationId"
        element={
          <RequireSession profile={profile}>
            <LevelSelect profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/practica/:operationId/:levelId"
        element={
          <RequireSession profile={profile}>
            <Practice profile={profile} onUpdate={refresh} />
          </RequireSession>
        }
      />
      <Route
        path="/resultados"
        element={
          <RequireSession profile={profile}>
            <Results profile={profile} />
          </RequireSession>
        }
      />
      <Route
        path="/upgrade"
        element={
          <RequireSession profile={profile}>
            <Upgrade />
          </RequireSession>
        }
      />
      <Route
        path="/billing/success"
        element={
          <RequireSession profile={profile}>
            <BillingSuccess />
          </RequireSession>
        }
      />
      <Route
        path="/mi-suscripcion"
        element={
          <RequireSession profile={profile}>
            <MiSuscripcion />
          </RequireSession>
        }
      />
      <Route path="/terminos" element={<Terminos />} />
      <Route
        path="/admin"
        element={
          <RequireSession profile={profile}>
            <AdminStats />
          </RequireSession>
        }
      />
      <Route
        path="/ranking"
        element={
          <RequireSession profile={profile}>
            <Ranking />
          </RequireSession>
        }
      />
      <Route
        path="/medallas"
        element={
          <RequireSession profile={profile}>
            <Medallas profile={profile} onReset={refresh} />
          </RequireSession>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppInner() {
  const { status } = useAuth();
  const [tick, setTick] = useState(0);
  // Read fresh from localStorage on every render so cloud-restore reflects immediately
  // The tick + status deps force re-render when external changes happen
  // eslint-disable-next-line no-unused-vars
  const _refreshDeps = [status, tick];
  const profile = loadProfile();
  const refresh = () => setTick((t) => t + 1);

  return (
    <div className="App">
      <Routed profile={profile} refresh={refresh} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <EntitlementProvider>
          <AppInner />
        </EntitlementProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
